import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useAddToast } from '../../../contexts/ToastContext';
import * as SavedFiltersStore from '../../../redux/savedFilters';
import useFetchIssuesAction from '../../issues/IssuesInbox/hooks/useFetchIssuesAction';
import type { Category } from '../../../../shared/issuesV2';
import type { NamedFilter } from '../../../../shared/savedFilters/common';
import type { ReduxStore } from '../../../redux/store';
import type { AsyncStatus } from '../../../types';

type UseSavedFilters = {
  filters: NamedFilter[];
  status: AsyncStatus;
};

/**
 * Handles fetching the savedFilters and returning the list,
 * as well as asyncStatus info. Re-fetches if the category changes.
 * Note that this all happens in Redux, this component is just a
 * little wrapper around all of that.
 *
 * @param {Category} category - A given issue category.
 * @param {boolean} [shouldSkipSummaryFetch] - By default, the issuesV2 fetchIssues action is dispatched when category changes. (@default false)
 */
const useSavedFilters = (category: Category, shouldSkipSummaryFetch = false): UseSavedFilters => {
  const dispatch = useDispatch();
  const setCurrentFilter = useFetchIssuesAction(SavedFiltersStore.actions.setCurrentFilter, shouldSkipSummaryFetch);
  const filters = useSelector<ReduxStore>((state) =>
    SavedFiltersStore.selectors.getFiltersByCategory(state, category)) as NamedFilter[];
  const status = useSelector(SavedFiltersStore.selectors.getGetFiltersAsyncStatus);

  const addToast = useAddToast();

  useEffect(() => {
    setCurrentFilter(null);
    dispatch(SavedFiltersStore.actions.getFilters(category, addToast));
  // eslint-disable-next-line react-hooks/exhaustive-deps -- setCurrentFilter is stable
  }, [dispatch, category]);

  return {
    filters: filters || [],
    status,
  };
};

export default useSavedFilters;
